import {useParams, useSearchParams} from "react-router-dom";
import {useContext, useEffect, useMemo, useRef, useState} from "react";
import {HalfwayContext} from "../context/HalfwayContext";
import {HalfwayContextType} from "../types/HalfwayContextType";
import {isNullOrWhitespace} from "../core/helpers/stringHelper";
import {MenuCategoryList} from "../components/menu/menuCategoryList";
import Header from "../components/menu/Header";
import {formatToRandWithDecimalsString} from "../core/helpers/textFormatting";
import {useSpring, animated} from "react-spring";
import {ProductDrawer} from "../components/drawers/ProductDrawer";
import {BasketDrawer} from "../components/drawers/BasketDrawer";
import {isMenuItemAvailable, resolveOnCourseContext} from "../core/helpers/onCourseContext";
import {getCategoryAnchor, orderMenuCategories} from "../core/helpers/menuCategoryNavigation";


export const Menu = () => {
    const {
        setCourseId,
        setCourseSection,
        setOnCourse,
        setCourseHole,
        categories,
        menuItems,
        onCourse,
        showProductDrawer,
        showBasketDrawer,
        toggleBasketDrawer,
        totalCostOfItemsInBasket,
        getCourseInfoFromApi,
        getCategoriesFromApi,
        getMenuItemsFromApi,
        getOptionSetsFromApi,
        resetCourseData,
    } = useContext(HalfwayContext) as HalfwayContextType;

    const urlParams = useParams();
    const [searchParams] = useSearchParams();
    const courseIdParam = urlParams?.courseId ?? '';
    const sectionParam = searchParams.get('section') ?? '';
    const onCourseParam = searchParams.get('oncourse');
    const holeParam = searchParams.get('hole');
    const {onCourse: isOnCourse, hole: courseHole} = resolveOnCourseContext(onCourseParam, holeParam);
    const courseLoadersRef = useRef({
        setCourseId,
        setCourseSection,
        setOnCourse,
        setCourseHole,
        resetCourseData,
        getCourseInfoFromApi,
        getCategoriesFromApi,
        getMenuItemsFromApi,
        getOptionSetsFromApi
    });
    courseLoadersRef.current = {
        setCourseId,
        setCourseSection,
        setOnCourse,
        setCourseHole,
        resetCourseData,
        getCourseInfoFromApi,
        getCategoriesFromApi,
        getMenuItemsFromApi,
        getOptionSetsFromApi
    };

    const [selectedCategory, setSelectedCategory] = useState<string>();
    const availableMenuItems = useMemo(
        () => menuItems.filter(item => isMenuItemAvailable(item, onCourse)),
        [menuItems, onCourse]
    );
    const visibleCategories = useMemo(() => orderMenuCategories(categories.filter(category =>
        availableMenuItems.some(menuItem => menuItem.categoryId === category.id)
    )), [categories, availableMenuItems]);

    const productSlideAnimation = useSpring({
        from: { y: "100%" },
        to: {
            y: showProductDrawer ? "0%" : "100%",
        },
        config: { tension: 280, friction: 28 },
    });

    const basketSlideAnimation = useSpring({
        from: { y: "100%" },
        to: {
            y: showBasketDrawer ? "0%" : "100%",
        },
        config: { tension: 280, friction: 28 },
    });

    useEffect(() =>
    {
        const {
            setCourseId,
            setCourseSection,
            setOnCourse,
            setCourseHole,
            resetCourseData,
            getCourseInfoFromApi,
            getCategoriesFromApi,
            getMenuItemsFromApi,
            getOptionSetsFromApi
        } = courseLoadersRef.current;

        const courseId = courseIdParam;
        setSelectedCategory(undefined);
        setCourseId(courseId);
        resetCourseData();
        setCourseSection(sectionParam);
        setOnCourse(isOnCourse);
        setCourseHole(courseHole);

        if (!isNullOrWhitespace(courseId))
        {
            getCourseInfoFromApi(courseId);
            const unsubscribeCategories = getCategoriesFromApi(courseId);
            const unsubscribeMenuItems = getMenuItemsFromApi(courseId);
            const unsubscribeOptionSets = getOptionSetsFromApi(courseId);

            return () => {
                unsubscribeCategories();
                unsubscribeMenuItems();
                unsubscribeOptionSets();
            };
        }
    }, [courseIdParam, sectionParam, isOnCourse, courseHole])

    useEffect(() => {
        const previousBodyOverflow = document.body.style.overflow;
        const previousHtmlOverflow = document.documentElement.style.overflow;
        const drawerIsOpen = showProductDrawer || showBasketDrawer;

        document.body.style.overflow = drawerIsOpen ? 'hidden' : previousBodyOverflow;
        document.documentElement.style.overflow = drawerIsOpen ? 'hidden' : previousHtmlOverflow;

        return () => {
            document.body.style.overflow = previousBodyOverflow;
            document.documentElement.style.overflow = previousHtmlOverflow;
        };
    }, [showProductDrawer, showBasketDrawer]);

    return (
        <div className="min-h-screen bg-slate-50">
            <div className={`flex flex-col w-full`}>
                {/*Header*/}
                <div className='top-0'>
                    <Header />
                </div>

                {/*Sticky Menu refs*/}
                <div className="menu-nav_component">
                    <div className="menu-nav_items">
                        {visibleCategories.map((category) => (
                            <a
                                href={`#${encodeURIComponent(getCategoryAnchor(category))}`}
                                key={getCategoryAnchor(category)}
                                onClick={() => setSelectedCategory(getCategoryAnchor(category))}
                                aria-current={selectedCategory === getCategoryAnchor(category) ? 'location' : undefined}
                                className={`menu-nav_link ${selectedCategory === getCategoryAnchor(category) ? 'active' : ''}`}>
                                {category.name}
                            </a>
                        ))}
                    </div>
                </div>

                {/*Menu Items*/}
                <div>
                    {categories.length !== 0 &&
                        <MenuCategoryList categories={visibleCategories} menuItems={availableMenuItems} />
                    }
                </div>

                {/*View Basket Button*/}
                <div className="sticky bottom-0 z-20 p-4 bg-gradient-to-t from-slate-50 via-slate-50 to-transparent pt-8"
                     style={{paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))'}}>
                    <button className="w-full"
                            type='button'
                            onClick={toggleBasketDrawer}>
                        <div className='button'>
                            <span>View Basket — {formatToRandWithDecimalsString(totalCostOfItemsInBasket)}</span>
                        </div>
                    </button>
                </div>
            </div>

            {/*Product Info Component — Overlay*/}
            {showProductDrawer && (
                <div className="drawer-overlay"
                     onClick={() => {}} />
            )}

            {/*Product Info Component*/}
            <div className='h-full'>
                <animated.div style={{
                    ...productSlideAnimation,
                    position: 'fixed',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 1000,
                    height: '92dvh',
                    maxHeight: '92dvh',
                    borderRadius: '20px 20px 0 0',
                    // `hidden` creates a programmatically scrollable container. Mobile
                    // browsers can then scroll the entire sheet when a low option input
                    // receives focus, exposing the backdrop below it. `clip` preserves
                    // the rounded-corner clipping without creating another scroller.
                    overflow: 'clip',
                    boxShadow: showProductDrawer ? '0 -4px 24px 0 rgba(0, 0, 0, 0.12)' : 'none',
                }}>
                    <div style={{ backgroundColor: 'white', height: '100%', minHeight: 0 }}>
                        {/*This component slides in from the bottom of the screen*/}
                        <ProductDrawer />
                    </div>
                </animated.div>
            </div>

            {/*Basket Info Component — Overlay*/}
            {showBasketDrawer && (
                <div className="drawer-overlay"
                     onClick={() => {}} />
            )}

            {/*Basket Info Component*/}
            <animated.div style={{
                ...basketSlideAnimation,
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1000,
                height: '95dvh',
                maxHeight: '95dvh',
                borderRadius: '20px 20px 0 0',
                overflow: 'clip',
                boxShadow: showBasketDrawer ? '0 -4px 24px 0 rgba(0, 0, 0, 0.12)' : 'none',
            }}>
                <div style={{ backgroundColor: 'white', height: '100%', minHeight: 0 }}>
                    {/*This component slides in from the bottom of the screen*/}
                    <BasketDrawer />
                </div>
            </animated.div>

        </div>
    )

}
